import './Product.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import Price from '@/common/components/blocks/Price/Price';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import { ROLES } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';

import { ProductDefaultProps, ProductPropTypes } from './Product.proptypes';
import useProduct from './useProduct';

const Product = ({
  deliverableQuantity,
  deliveredQuantity,
  deliveredQuantityLabel,
  id,
  label,
  orderedQuantity,
  priceTTC,
  productBrand,
  productId,
  productPrice,
  returnedQuantity,
  saleUnit,
  salesPackaging,
  shippingDelay,
  splitPackaging,
  supplierReference,
  totalLineAmount,
  weeeTaxAmount,
  totalLineAmountTTC,
  weeeTotalTaxAmount,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const {
    getDeliveredQuantityLabel,
    getProductId,
    getSelectedQuantity,
  } = useProduct({
    deliverableQuantity,
    deliveredQuantity,
    deliveredQuantityLabel,
    orderedQuantity,
    productId,
    returnedQuantity,
  });

  return (
    <div className="order-item-product">
      <div className="order-item-product__brand">{productBrand}</div>
      <div className="order-item-product__label">{label}</div>
      <div className="order-item-product__sku">
        <span className="label">{t('ebat:entities:order.supplier_code')}</span>
        {getProductId}
      </div>
      <div className="order-item-product__reference">
        <span className="label">{t('ebat:entities:order.main_sku')}</span>
        {supplierReference}
      </div>
      <div className="order-item-product__ordered">
        <span className="label">
          {t('ebat:entities:order.ordered_quantity')}
        </span>
        <span className="value">
          {orderedQuantity}
          <span className="unit">{saleUnit}</span>
        </span>
      </div>
      <div className="order-item-product__deliverable">
        <span className="label">
          {t('ebat:entities:order.deliverable_quantity')}
        </span>
        <span className="value">
          {deliverableQuantity || '--'}
          {deliverableQuantity ? (
            <span className="unit">{saleUnit}</span>
          ) : null}
        </span>
      </div>
      <div className="order-item-product__delivered">
        <span className="label">
          {t('ebat:entities:order.delivered_quantity')}
        </span>
        <span className="value">
          {getDeliveredQuantityLabel}
          {!Number.isNaN(parseFloat(deliveredQuantityLabel)) ? (
            <span className="unit">{saleUnit}</span>
          ) : null}
        </span>
      </div>
      <div className="order-item-product__delay">
        <span className="label">{t('ebat:entities:order.shipping_delay')}</span>
        <span className="value">
          {shippingDelay ? i18n.format(shippingDelay * 1, 'short') : '--'}
        </span>
      </div>
      <div className="order-item-product__quantity">
        <span className="label">{t('ebat:entities:order.quantity')}</span>
        <Quantity
          disabled
          id={id}
          quantity={getSelectedQuantity}
          salesPackaging={salesPackaging}
          splitPackaging={splitPackaging}
          unit={saleUnit}
        />
      </div>
      <div className="order-item-product__price">
        <span className="label">
          {user?.role === ROLES.CUSTOMER_GUEST
            ? t('ebat:entities:order.priceTTC')
            : t('ebat:entities:order.price')}
        </span>
        <span className="value">
          <Price
            emptyLabel={t('common:entities:product.price.not_price')}
            value={
              user?.role === ROLES.CUSTOMER_GUEST ? priceTTC : productPrice
            }
          />
        </span>
        {weeeTaxAmount > 0 && (
          <span className="price__weee">
            {t('common:entities:product.price.weee_amount_long', {
              amount: (
                <Price
                  className="amount"
                  key={`weee-amount-${weeeTaxAmount}`}
                  sup={false}
                  value={weeeTaxAmount}
                />
              ),
            })}
          </span>
        )}
      </div>
      <div className="order-item-product__total">
        <span className="label">
          {user?.role !== ROLES.CUSTOMER_GUEST
            ? t('ebat:entities:order.total')
            : t('ebat:entities:order.totalTTC')}
        </span>
        <span className="value">
          <Price
            value={
              user?.role === ROLES.CUSTOMER_GUEST
                ? totalLineAmountTTC
                : totalLineAmount
            }
          />
        </span>
        {weeeTotalTaxAmount > 0 && (
          <span className="total__weee">
            {t('common:entities:product.price.weee_amount_long', {
              amount: (
                <Price
                  className="amount"
                  key={`weee-amount-${weeeTotalTaxAmount}`}
                  sup={false}
                  value={weeeTotalTaxAmount}
                />
              ),
            })}
          </span>
        )}
      </div>
    </div>
  );
};

Product.defaultProps = {
  ...ProductDefaultProps,
};

Product.propTypes = {
  ...ProductPropTypes,
};

export default Product;
